/**
 * Settings -> Forms. Admins build forms from a bank of questions; staff send
 * them from a record (a claim today) and the recipient answers by link.
 *
 * Questions are fields on the tenant's `form_request` entity, created with the
 * Entity Editor's shared attribute dialog in its question mode. Which
 * questions a form asks, and when each one shows, lives on the form, edited
 * in the form builder (form-builder-page.tsx).
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { DataTable, type DataTableColumn } from "@/admin/components/data-table";
import { ConfirmationDialog } from "@/admin/components/shared/confirmation-dialog";
import { useDeleteEntityAttribute, useEntityTypes } from "@/admin/hooks/use-entities";
import {
  useFormTemplates,
  type FormResponseSet,
  type FormTemplate,
} from "@/admin/hooks/use-forms";
import { FormResponseSetDialog } from "@/admin/pages/settings/form-response-set-dialog";
import {
  setOptions,
  typeLabel,
  useFormQuestionBank,
  type Question,
} from "@/admin/pages/settings/form-question-bank";
import { usePermissions } from "@/admin/hooks/use-permissions";
import { EntityAttributeDialog } from "@/admin/pages/manage/entities/entity-editor-page";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { Separator } from "@/shared/components/ui/separator";
import { toastApiError } from "@/shared/lib/api-error";
import { TOAST_DURATION } from "@/shared/lib/toast-duration";

function SectionHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle: string;
  action?: React.ReactNode;
}) {
  return (
    <header className="mb-5 flex flex-wrap items-end justify-between gap-4">
      <div className="max-w-[58ch]">
        <h2 className="clara-heading text-[20px]">{title}</h2>
        <p className="mt-1 text-[13px] text-[var(--ink-3)]">{subtitle}</p>
      </div>
      {action}
    </header>
  );
}

// Each list scrolls in its own box instead of stretching the page.
const LIST_HEIGHT = "max-h-[40vh]";

/** What questions, response sets and forms are, in the order an admin builds them. */
function FormsHelp() {
  const { t } = useTranslation();
  const steps = [
    {
      title: t("forms.settings.questions", "Questions"),
      body: t(
        "forms.settings.helpQuestions",
        "Write each question once and reuse it on any form.",
      ),
    },
    {
      title: t("forms.settings.responseSets", "Response sets"),
      body: t(
        "forms.settings.helpResponseSets",
        "Reusable answer lists, like Pass or Fail. Edit a set and its questions follow.",
      ),
    },
    {
      title: t("forms.settings.title", "Forms"),
      body: t(
        "forms.settings.helpForms",
        "Pick questions, group them into sections, then publish to send.",
      ),
    },
  ];
  return (
    <section
      className="mb-10 rounded-lg border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] p-5"
      data-testid="forms-help"
    >
      <h2 className="clara-label">{t("forms.settings.helpTitle", "How forms work")}</h2>
      <ol className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-3">
        {steps.map((step, index) => (
          <li key={step.title}>
            <p className="text-[13px] font-medium text-[var(--ink)]">
              {index + 1}. {step.title}
            </p>
            <p className="mt-1 text-[12px] text-[var(--ink-3)]">{step.body}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

export function FormsTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { can } = usePermissions();
  const canEdit = can("templates.edit.global") || can("templates.edit.acl");
  const navigate = useNavigate();
  const templatesQuery = useFormTemplates();
  const templates = templatesQuery.data ?? [];
  const { entityQuery, entity, setsQuery, responseSets, questions, answerTypes } =
    useFormQuestionBank();
  const { data: allEntities = [] } = useEntityTypes();
  const deleteQuestion = useDeleteEntityAttribute(entity?.uuid);
  const [editingSet, setEditingSet] = useState<FormResponseSet | "new" | null>(null);
  const [questionDialog, setQuestionDialog] = useState<{
    open: boolean;
    attribute?: Question;
  }>({ open: false });
  const [deleting, setDeleting] = useState<Question | null>(null);

  // Listing templates creates the question entity on first use; re-check
  // the registry so "New question" appears without a reload.
  const loaded = templatesQuery.isSuccess;
  useEffect(() => {
    if (loaded && !entity) qc.invalidateQueries({ queryKey: ["entity-lookup"] });
  }, [loaded, entity, qc]);

  const setNames = useMemo(
    () => new Map(responseSets.map((s) => [s.key, s.name])),
    [responseSets],
  );

  const deleteText = (question: Question) => {
    const total = templates.filter((tpl) =>
      tpl.questions.some((q) => q.code === question.code_name),
    ).length;
    if (total === 0) return t("forms.settings.deleteUnused", "It isn't on any form.");
    if (total === 1) {
      return t(
        "forms.settings.deleteUsedOnce",
        "It's on 1 form. Deleting it takes it off that form.",
      );
    }
    return t(
      "forms.settings.deleteUsedMany",
      "It's on {{total}} forms. Deleting it takes it off them.",
      { total },
    );
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await deleteQuestion.mutateAsync({ attrUuid: deleting.uuid });
      qc.invalidateQueries({ queryKey: ["form-templates"] });
      toast.success(t("forms.settings.deleted", "Question deleted"));
    } catch (err) {
      if ((err as { response?: { status?: number } })?.response?.status === 409) {
        toast.error(
          t(
            "forms.settings.deleteHasAnswers",
            "This question has answers on forms already sent, so it can't be deleted.",
          ),
          { duration: TOAST_DURATION.error },
        );
      } else {
        toastApiError(err, t("forms.settings.deleteFailed", "Could not delete the question."));
      }
    }
    setDeleting(null);
  };

  const templateColumns: DataTableColumn<FormTemplate>[] = [
    { id: "name", header: t("forms.settings.colName", "Form"), accessorKey: "name" },
    {
      id: "questions",
      header: t("forms.settings.colQuestions", "Questions"),
      cell: (row) => row.questions.length,
    },
    {
      // The version sends use, and whether the draft has moved on since.
      id: "published",
      header: t("forms.settings.colPublished", "Published"),
      cell: (row) =>
        row.published_version === null ? (
          <Badge variant="outline">{t("forms.settings.notPublished", "Not published")}</Badge>
        ) : (
          <span className="flex items-center gap-2">
            {t("forms.settings.versionN", "Version {{number}}", {
              number: row.published_version,
            })}
            {row.has_draft_changes ? (
              <Badge variant="outline">{t("forms.settings.draftChanges", "Draft changes")}</Badge>
            ) : null}
          </span>
        ),
    },
    {
      id: "status",
      header: t("forms.settings.colStatus", "Status"),
      cell: (row) =>
        row.is_active ? (
          <Badge variant="secondary">{t("forms.settings.statusActive", "Active")}</Badge>
        ) : (
          <Badge variant="outline">{t("forms.settings.statusOff", "Off")}</Badge>
        ),
    },
  ];

  const setColumns: DataTableColumn<FormResponseSet>[] = [
    { id: "name", header: t("forms.settings.name", "Name"), accessorKey: "name" },
    {
      id: "answers",
      header: t("forms.settings.answers", "Answers"),
      cell: (row) => row.options.map((answer) => answer.label).join(", "),
    },
    {
      id: "status",
      header: t("forms.settings.colStatus", "Status"),
      cell: (row) =>
        row.is_active ? (
          <Badge variant="secondary">{t("forms.settings.statusActive", "Active")}</Badge>
        ) : (
          <Badge variant="outline">{t("forms.settings.statusOff", "Off")}</Badge>
        ),
    },
  ];

  const questionColumns: DataTableColumn<Question>[] = [
    {
      id: "question",
      header: t("forms.settings.colQuestion", "Question"),
      accessorKey: "display_name",
    },
    {
      id: "type",
      header: t("forms.settings.colType", "Type"),
      cell: (row) => {
        const set = setNames.get(String(row.configuration_data?.response_set ?? ""));
        return (
          <div className="flex flex-wrap gap-1">
            <Badge variant="outline" className="font-normal">
              {typeLabel(answerTypes, row.field_type)}
            </Badge>
            {set ? <Badge variant="secondary" className="font-normal">{set}</Badge> : null}
          </div>
        );
      },
    },
    ...(canEdit
      ? [
          {
            id: "delete",
            header: "",
            cell: (row: Question) => (
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={t("forms.settings.deleteQuestion", "Delete question")}
                onClick={(event) => {
                  event.stopPropagation();
                  setDeleting(row);
                }}
                data-testid={`forms-delete-question-${row.code_name}`}
              >
                <Trash2 size={14} />
              </Button>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="pb-16" data-testid="forms-settings">
      <FormsHelp />
      <SectionHeader
        title={t("forms.settings.title", "Forms")}
        subtitle={t("forms.settings.subtitle", "Email a form to anyone from a claim.")}
        action={
          canEdit ? (
            <Button onClick={() => navigate("/settings/forms/new")} data-testid="forms-new">
              <Plus size={14} />
              {t("forms.settings.newForm", "New form")}
            </Button>
          ) : null
        }
      />
      <DataTable
        tableId="form-templates"
        columns={templateColumns}
        data={templates}
        isLoading={templatesQuery.isPending}
        isFetching={templatesQuery.isFetching}
        rowKey="uuid"
        onRowClick={canEdit ? (row) => navigate(`/settings/forms/${row.key}`) : undefined}
        emptyMessage={t("forms.settings.emptyForms", "No forms yet.")}
        maxHeightClass={LIST_HEIGHT}
      />

      <Separator className="my-14 bg-[var(--rule-strong)]" />

      <SectionHeader
        title={t("forms.settings.questions", "Questions")}
        subtitle={t(
          "forms.settings.questionsHint",
          "One bank of questions for every form. A question can be used on several forms.",
        )}
        action={
          canEdit && entity ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setQuestionDialog({ open: true })}
              data-testid="forms-add-question"
            >
              <Plus size={14} />
              {t("forms.settings.newQuestion", "New question")}
            </Button>
          ) : null
        }
      />
      <DataTable
        tableId="form-questions"
        columns={questionColumns}
        data={questions}
        isLoading={entityQuery.isConfigLoading || !entity}
        isFetching={entityQuery.isFetching}
        rowKey="uuid"
        onRowClick={
          canEdit ? (attr) => setQuestionDialog({ open: true, attribute: attr }) : undefined
        }
        emptyMessage={t("forms.settings.emptyQuestions", "No questions yet.")}
        maxHeightClass={LIST_HEIGHT}
      />

      <Separator className="my-14 bg-[var(--rule-strong)]" />

      <SectionHeader
        title={t("forms.settings.responseSets", "Response sets")}
        subtitle={t(
          "forms.settings.responseSetsHint",
          "Shared answer lists for choice questions.",
        )}
        action={
          canEdit ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditingSet("new")}
              data-testid="forms-new-response-set"
            >
              <Plus size={14} />
              {t("forms.settings.newResponseSet", "New response set")}
            </Button>
          ) : null
        }
      />
      <DataTable
        tableId="form-response-sets"
        columns={setColumns}
        data={responseSets}
        isLoading={setsQuery.isPending}
        isFetching={setsQuery.isFetching}
        rowKey="uuid"
        onRowClick={canEdit ? (row) => setEditingSet(row) : undefined}
        emptyMessage={t("forms.settings.emptyResponseSets", "No response sets yet.")}
        maxHeightClass={LIST_HEIGHT}
      />

      {editingSet ? (
        <FormResponseSetDialog
          responseSet={editingSet === "new" ? undefined : editingSet}
          onClose={() => setEditingSet(null)}
        />
      ) : null}
      {entity ? (
        <EntityAttributeDialog
          entity={{
            ...entity,
            relationships: entity.relationships ?? [],
            attributes: entity.attributes ?? [],
          }}
          allEntities={allEntities.length ? allEntities : [entity]}
          attribute={questionDialog.attribute}
          open={questionDialog.open}
          onOpenChange={(open) => setQuestionDialog((s) => ({ ...s, open }))}
          defaultOrder={questions.length}
          defaults={{ source: "EAV" }}
          answerTypes={answerTypes}
          responseSets={responseSets
            .filter(
              (s) =>
                s.is_active ||
                s.key === questionDialog.attribute?.configuration_data?.response_set,
            )
            .map((s) => ({ key: s.key, name: s.name, options: setOptions(s) }))}
        />
      ) : null}
      <ConfirmationDialog
        open={Boolean(deleting)}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={t("forms.settings.deleteTitle", "Delete {{name}}?", {
          name: deleting?.display_name ?? "",
        })}
        description={deleting ? deleteText(deleting) : undefined}
        confirmLabel={t("common.delete", "Delete")}
        cancelLabel={t("common.cancel", "Cancel")}
        pendingLabel={t("forms.settings.deleting", "Deleting...")}
        destructive
        isPending={deleteQuestion.isPending}
        onConfirm={confirmDelete}
      />
    </div>
  );
}

export default FormsTab;
